"use client";

import { logger } from "@scott/logger";
import type { ReactNode, SyntheticEvent } from "react";
import { useRef, useState, useTransition } from "react";
import { Alert } from "../../elements/alert/alert";
import { Button } from "../../elements/button/button";
import { Text } from "../../elements/text/text";
import { IconCart, IconLoader2 } from "../../icons";
import type {
  ProductSizeNotice,
  ProductSizeOption,
} from "../../patterns/product-sizes/product-sizes";
import { ProductSizes } from "../../patterns/product-sizes/product-sizes";
import { cn } from "../../utils/cn";

export type UseNavigationPromptOptions = {
  enabled: boolean;
  showDialog: () => Promise<boolean>;
  // List of pathnames/strings that should not trigger the navigation warning
  // e.g. specific routes or query parameters that should bypass the warning prompt
  exceptionList?: string[];
};

// Type for each variant in the multi-product mock
interface ProductVariant {
  colorName: string;
  lensName?: string;
  yourPrice: string;
  normalPrice?: string;
  discountLabel?: string;
  discountPrice?: string;
  isNetPriced?: boolean;
  imageUrl: string;
  sizes: ProductSizeOption[];
  href?: string; // Optional href for navigation with query params
  brand?: string; // Optional brand field for GTM tracking
}

export type ProductBoxViewItem = {
  productName: string;
  productCode: string;
  variantId: string;
  variantName: string;
  size: string;
  price: string;
  brand: string;
};

// Response type from addToCartAction
export type AddToCartActionResponse =
  | {
      status: "success";
      resetKey: number;
      errorMessage?: string;
    }
  | {
      status: "error";
      resetKey: number;
      errorMessage: string;
    };

export interface ProductBoxProps {
  country?: string;
  microcopies: {
    addToCart: string;
    details?: string;
    productCode: string;
    yourPriceLabel: string;
    rrpLabel: string;
    helperMessage4?: string;
    orderLimitReachedTitle: string;
    orderLimitReachedDescription: string;
    fromLabel?: string;
    frameLabel?: string;
    lensLabel?: string;
    tagNetPriced?: string;
  };
  header: {
    productName: string;
    productCode: string;
    imageUrl: string;
    imageAlt: string;
  };
  variants: ProductVariant[];
  currency?: string;
  onViewItem?: (item: ProductBoxViewItem) => void;
  /** Keyed by size `productId`; shared across a PLP so each size is only reported once. */
  seenMap?: Map<string, { productId: string }>;
  footer: {
    addToCartLabel: string;
    updateCartLabel: string;
  };
  products?: Record<string, ProductBoxProps>;
  addToCartAction?: (
    formData: FormData,
    baseSiteId?: string,
    cartId?: string
  ) => Promise<AddToCartActionResponse>;
  baseSiteId?: string;
  cartId?: string;
  hasCartItems?: boolean;
  showEanCode?: boolean;
  /** Session-only "Hide Dealer Prices" toggle — hides dealer price, RRP stays visible */
  hideDealerPrice?: boolean;
  /**
   * COMPB2B-3046 (FR04): true for the Shop Viewer dealer role. Viewers can
   * browse products but can't purchase — quantity inputs are disabled and
   * the Add to Cart footer (button + helper message) is hidden entirely.
   */
  isReadOnly?: boolean;
  onProductSizeClick?: (href: string) => void;
  showNavigationWarning?: (props: UseNavigationPromptOptions) => void;
  displayNavigationWarning?: (message?: string | undefined) => Promise<boolean>;
  renderSizeNotice?: (notice: ProductSizeNotice) => ReactNode;
}

type QuantityBaselineMap = Record<string, number>;

function extractQuantities(formData: FormData): QuantityBaselineMap {
  const nextBaselines: QuantityBaselineMap = {};

  // Capture numeric form fields keyed by variant code so a successful submit
  // can preserve the latest user-entered quantities as the next local baseline.
  formData.forEach((value, key) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      nextBaselines[key] = parsed;
    }
  });

  return nextBaselines;
}

export function ProductBox(props: Readonly<ProductBoxProps>): ReactNode {
  const [, startTransition] = useTransition();
  const [state, setState] = useState({
    resetKey: 0,
    isSubmitting: false,
    isDirty: false,
    hasBeenAddedToCart: props.hasCartItems ?? false,
    quantityBaselines: {} as QuantityBaselineMap,
  });
  const formRef = useRef<HTMLFormElement>(null);
  const productStatesRef = useRef<Map<string, boolean>>(new Map());

  // Reports a size to `onViewItem` only once it actually scrolls into view
  // (see `SizeViewTracker` in `ProductSizes`), rather than for every rendered
  // size on mount — matches pre-order's viewport-gated view_item_list logic.
  const buildOnSizeInView =
    (variant: ProductVariant) => (size: ProductSizeOption) => {
      props.onViewItem?.({
        productName: props.header.productName,
        productCode: props.header.productCode,
        variantId: size.productId,
        variantName: [variant.colorName, variant.lensName]
          .filter(Boolean)
          .join("/"),
        size: size.size,
        price: size.priceInfo?.value || variant.yourPrice,
        brand: variant.brand || "",
      });
    };

  const handleProductStateChange = (productId: string, isModified: boolean) => {
    productStatesRef.current.set(productId, isModified);

    // Check if ANY product is modified
    const anyModified = Array.from(productStatesRef.current.values()).some(
      Boolean
    );

    // Use queueMicrotask to defer state update and avoid "Cannot update during render" error
    queueMicrotask(() => {
      setState((prev) => {
        // Only update if dirty state has changed to avoid unnecessary re-renders
        if (prev.isDirty !== anyModified) {
          return { ...prev, isDirty: anyModified };
        }
        return prev;
      });
    });
  };

  if (props.displayNavigationWarning) {
    props.showNavigationWarning?.({
      enabled: state.isDirty,
      showDialog: props.displayNavigationWarning,
    });
  }

  function handleFormSubmit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    // Snapshot submitted quantities before async action to avoid remounting
    // inputs with stale values when `resetKey` changes on success.
    const submittedQuantities = extractQuantities(formData);

    // Set submitting state
    setState((prev) => ({ ...prev, isSubmitting: true }));

    // If addToCartAction is provided, call it inside startTransition
    if (props.addToCartAction) {
      const addToCartAction = props.addToCartAction;
      startTransition(async () => {
        try {
          const result = await addToCartAction(
            formData,
            props.baseSiteId,
            props.cartId
          );

          if (result.status === "success") {
            const hasItemsInCart = Object.values(submittedQuantities).some(
              (qty) => qty > 0
            );

            // Update resetKey to reset form on success
            setState((prev) => ({
              ...prev,
              resetKey: result.resetKey,
              isSubmitting: false,
              isDirty: false,
              hasBeenAddedToCart: hasItemsInCart,
              quantityBaselines: {
                ...prev.quantityBaselines,
                ...submittedQuantities,
              },
            }));
          } else if (result.status === "error") {
            // Reset isDirty on error — SAP has already applied the mutation
            // (possibly partially). Keeping dirty would trigger the navigation
            // warning and cause a stuck layout-level spinner.
            setState((prev) => ({
              ...prev,
              isSubmitting: false,
              isDirty: false,
            }));
            logger.error("Add to cart error:", result.errorMessage);
          }
        } catch (error) {
          // Handle unexpected errors
          setState((prev) => ({
            ...prev,
            isSubmitting: false,
            isDirty: false,
          }));
          logger.error("Add to cart failed:", error);
        }
      });
    } else {
      // Fallback: just log and reset state if no action provided
      logger.warn("No addToCartAction provided");
      setState((prev) => ({
        ...prev,
        isSubmitting: false,
      }));
    }
  }

  // If products collection is provided, render multiple products
  if (props.products) {
    const entries = Object.entries(props.products);
    return (
      <div className="mb-8 space-y-6">
        {entries.map(([key, product]) => (
          <div key={key}>
            <ProductBox
              {...product}
              currency={props.currency}
              onViewItem={props.onViewItem}
              renderSizeNotice={props.renderSizeNotice}
              seenMap={props.seenMap}
            />
          </div>
        ))}
      </div>
    );
  }

  // Otherwise render single product
  const header = props.header;
  const variants = props.variants;

  return (
    <form
      data-testid="product-form"
      key={state.resetKey}
      onSubmit={handleFormSubmit}
      ref={formRef}
    >
      <div className="overflow-hidden rounded-lg border border-stone-200">
        <div className="flex w-full flex-col items-start gap-1 border-b bg-white p-4">
          <div className="flex w-full items-center gap-3">
            {props.onProductSizeClick && variants[0]?.href ? (
              <button
                className="cursor-pointer text-left"
                onClick={() => {
                  const href = variants[0]?.href;
                  if (href) {
                    props.onProductSizeClick?.(href);
                  }
                }}
                type="button"
              >
                <Text variant="body-md-bold">{header.productName}</Text>
              </button>
            ) : (
              <Text variant="body-md-bold">{header.productName}</Text>
            )}
          </div>
          <div className="flex w-full items-center gap-2">
            <Text variant="body-md">
              {props.microcopies.productCode}: {header.productCode}
            </Text>
          </div>
        </div>

        <div className="flex flex-col gap-1 bg-white p-2">
          {variants.map((variant: ProductVariant, idx: number) => {
            // RRP is always returned by OCC regardless of the "Hide Dealer
            // Prices" toggle (ADR-0018), so its presence tells us the empty
            // yourPrice is just the dealer price being toggled off, not a
            // genuinely priceless product that should stay disabled.
            const dealerPriceHiddenByToggle =
              props.hideDealerPrice && Boolean(variant.normalPrice);
            const sizes = variant.sizes.map((size) => ({
              ...size,
              quantityProps: {
                ...size.quantityProps,
                disabled:
                  props.isReadOnly ||
                  (variant.yourPrice === "" && !dealerPriceHiddenByToggle) ||
                  size.quantityProps.disabled,
                value:
                  state.quantityBaselines[size.productId] ??
                  size.quantityProps.value,
              },
              priceInfo: size.priceInfo
                ? {
                    ...size.priceInfo,
                    showVariantPrice:
                      size.priceInfo.showVariantPrice !== false &&
                      !props.hideDealerPrice,
                  }
                : size.priceInfo,
            }));
            const variantNotice = sizes.find((size) => size.notice)?.notice;

            const keyId =
              variant.sizes?.[0]?.productId ?? `${header.productCode}-${idx}`;
            return (
              <div key={String(keyId)}>
                <div className="bg-white">
                  {variantNotice
                    ? props.renderSizeNotice?.(variantNotice)
                    : null}
                  <div
                    className={cn(
                      "px-1 pt-1 pb-2",
                      idx < variants.length - 1 && "border-b"
                    )}
                  >
                    <div className="flex items-start gap-4">
                      <div className="min-w-0 flex-1">
                        <ProductSizes
                          country={props.country}
                          handleButtonClick={
                            props.onProductSizeClick ?? (() => {})
                          }
                          microcopies={{
                            orderLimitReachedTitle:
                              props.microcopies.orderLimitReachedTitle,
                            orderLimitReachedDescription:
                              props.microcopies.orderLimitReachedDescription,
                            fromLabel: props.microcopies.fromLabel,
                          }}
                          microcopyLabels={{
                            frameLabel: props.microcopies.frameLabel,
                            lensLabel: props.microcopies.lensLabel,
                          }}
                          onProductStateChange={handleProductStateChange}
                          onSizeInView={buildOnSizeInView(variant)}
                          priceInfo={{
                            label: props.microcopies.yourPriceLabel,
                            value: variant.yourPrice,
                            showRrp: true,
                            rrpLabel: props.microcopies.rrpLabel,
                            rrpValue: variant.normalPrice,
                            discountLabel: variant.discountLabel,
                            discountPrice: variant.discountPrice,
                            isNetPriced: variant.isNetPriced,
                            showVariantPrice: !props.hideDealerPrice,
                          }}
                          productInfo={{
                            imageUrl: variant.imageUrl,
                            imageAlt: header.imageAlt,
                            colorName: variant.colorName,
                            lensName: variant.lensName,
                            productName: header.productName,
                            productCode: header.productCode,
                            href: variant.href, // Pass href with query params to ProductSizes
                          }}
                          seenMap={props.seenMap}
                          showEanCode={props.showEanCode}
                          sizes={sizes}
                          tagNetPriced={props.microcopies.tagNetPriced}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* COMPB2B-3046 (FR04): Shop Viewers are read-only — hide the whole
            purchase footer (Add to Cart CTA + helper message) entirely. */}
        {props.footer && !props.isReadOnly && (
          <div className="flex flex-col items-end justify-end gap-4 border-t bg-white p-4 md:flex-row md:items-center">
            {/*  Helper message - The user has not added any quantity */}

            <div>
              <Alert
                microcopies={{ title: props.microcopies.helperMessage4 ?? "" }}
                variant="default"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                disabled={state.isSubmitting || !state.isDirty}
                size="sm"
                type="submit"
                variant="default"
              >
                {state.isSubmitting ? (
                  <IconLoader2 className="animate-spin" size="sm" />
                ) : (
                  <IconCart size="sm" />
                )}
                <Text variant="body-md">
                  {state.hasBeenAddedToCart && props.footer.updateCartLabel
                    ? props.footer.updateCartLabel
                    : props.footer.addToCartLabel}
                </Text>
              </Button>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
